import { useState, useEffect, useCallback, useRef } from 'react';
import communityApi from '../api';

const POLL_INTERVAL = 30_000; // 30 seconds

export const useNotifications = ({ enabled = true } = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await communityApi.notifications.getUnreadCount();
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  }, [enabled]);

  const fetchNotifications = useCallback(async (params = {}) => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await communityApi.notifications.getAll({ 
        page_size: params.page_size || 20,
        page: params.page || 1
      });
      if (params.page > 1) {
        setNotifications(prev => [...prev, ...(res.data?.results ?? [])]);
      } else {
        setNotifications(res.data?.results ?? []);
      }
      // Also refresh unread count to be sure
      await fetchUnreadCount();
      return res.data;
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [enabled, fetchUnreadCount]);

  const markAsRead = useCallback(async (id) => {
    try {
      await communityApi.notifications.markRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      await communityApi.notifications.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
    }
  }, []);

  // Poll for unread count
  useEffect(() => {
    if (!enabled) return () => {};
    
    fetchUnreadCount();
    intervalRef.current = setInterval(fetchUnreadCount, POLL_INTERVAL);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
  };
};
